<template>
  <div class="w-32 h-32">
    <Doughnut
      :data="chartData"
      :options="chartOptions"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="plugins"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />
  </div>
</template>

<script setup lang="ts">
import { australianCurrency } from '@/helpers';
import { ArcElement, Chart as ChartJS, Legend, Plugin, Tooltip } from 'chart.js';
import { computed } from 'vue';
import { Doughnut } from 'vue-chartjs';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
);

const props = defineProps<{
  claimCertifiedTotal: number;
  remainingBudget: number;
  totalBudget: number;
  variationOrdersTotalApproved: number;
}>();

// Chart configuration
const chartId = 'completion-donut-chart';
const datasetIdKey = 'label';
const cssClasses = '';
const styles = {};
const width = undefined;
const height = undefined;

const completionPercentagePlugin: Plugin<'doughnut'> = {
  id: 'completionPercentage',
  beforeDatasetDraw: (chart) => {
    const ctx = chart.ctx;
    const total = props.totalBudget + props.variationOrdersTotalApproved;
    const percentage = total > 0 ? ((props.claimCertifiedTotal / total) * 100).toFixed(1) : '0';

    ctx.font =
      'bold 18px ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"';

    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = '#111827'; // text-gray-900
    ctx.fillText(`${percentage}%`, chart.width / 2, chart.height / 2);

    ctx.font =
      '12px ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"';

    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = '#4b5563'; // text-gray-600
    ctx.fillText('Complete', chart.width / 2, chart.height / 2 + 20);

    ctx.save();
  },
};

const plugins = [completionPercentagePlugin];

const chartData = computed(() => ({
  labels: [
    'Work Done',
    'Remaining',
  ],

  datasets: [{
    data: [props.claimCertifiedTotal, props.remainingBudget],
    backgroundColor: [
      'rgba(59, 130, 246, 0.8)',
      'rgba(239, 68, 68, 0.8)',
    ],

    borderColor: [
      'rgba(59, 130, 246, 1)',
      'rgba(239, 68, 68, 1)',
    ],

    borderWidth: 2,
    hoverOffset: 8,
  }],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  cutout: '80%',
  plugins: {
    legend: {
      display: false,
    },

    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      borderWidth: 1,
      cornerRadius: 8,
      callbacks: {
        label(context: any) {
          const total = props.totalBudget + props.variationOrdersTotalApproved;
          const percentage = total > 0 ? (context.parsed / total * 100).toFixed(1) : '0';

          return `${percentage}%`;
        },
      },
    },
  },

  elements: {
    arc: {
      borderWidth: 2,
    },
  },
}));
</script>

<style scoped></style>
